import React, { useMemo } from 'react';
import { Store as StoreIcon } from '@mui/icons-material';
import { getMediaDisplayUrl } from '../../utils/mediaUtils';
import { DEFAULT_PRESENTATION_SETTINGS } from '../../constants/presentationSettings';
import './ShopAppearancePreview.css';

const PLACEHOLDER_PRODUCTS = [
  { name: 'Vase artisanal', price: '45 €', description: 'Céramique émaillée' },
  { name: 'Bol rustique', price: '28 €', description: 'Grès tourné' },
  { name: 'Assiette décorée', price: '32 €', description: 'Motif floral' },
  { name: 'Tasse à thé', price: '22 €', description: 'Fait main' },
];

const HEADING_SCALE = { small: 0.9, medium: 1, large: 1.15 };
const BODY_SCALE = { small: 0.9, normal: 1, large: 1.1 };

const ASPECT_RATIOS = {
  '1:1': '1 / 1',
  '4:3': '4 / 3',
  '3:4': '3 / 4',
  '16:9': '16 / 9',
};

const ShopAppearancePreview = ({
  name = '',
  title = '',
  slug = '',
  logo = null,
  banner = null,
  presentation = DEFAULT_PRESENTATION_SETTINGS,
  activeField = null,
  onFieldSelect = () => {},
  onNameChange = () => {},
  onTitleChange = () => {},
  interactive = true,
}) => {
  const bannerUrl = getMediaDisplayUrl(banner);
  const logoUrl = getMediaDisplayUrl(logo);
  const displayName = name.trim() || 'Nom de votre boutique';
  const displayTitle = title.trim() || 'Votre slogan ou accroche';

  const { layout, theme, typography, productDisplay } = presentation;

  const previewStyle = useMemo(
    () => ({
      '--preview-bg': theme.backgroundColor,
      '--preview-text': theme.textColor,
      '--preview-primary': theme.primaryColor,
      '--preview-secondary': theme.secondaryColor,
      '--preview-accent': theme.accentColor,
      '--preview-heading-scale': HEADING_SCALE[typography.headingSize] ?? 1,
      '--preview-body-scale': BODY_SCALE[typography.bodySize] ?? 1,
      '--preview-products-cols': layout.productsPerRow,
      '--preview-image-ratio': ASPECT_RATIOS[productDisplay.imageAspectRatio] ?? '1 / 1',
      fontFamily: `${typography.fontFamily}, system-ui, sans-serif`,
    }),
    [layout.productsPerRow, productDisplay.imageAspectRatio, theme, typography]
  );

  const zoneClass = (field) =>
    `shop-preview__zone ${interactive ? 'shop-preview__zone--interactive' : ''} ${
      activeField === field ? 'shop-preview__zone--active' : ''
    }`;

  const handleZoneClick = (field, event) => {
    if (!interactive) return;
    event.stopPropagation();
    onFieldSelect(field);
  };

  const showLayoutToolbar =
    layout.showSearch || layout.showFilters || (layout.showCategories && layout.categoriesPosition === 'top');

  return (
    <div className="shop-preview-frame">
      <div className="shop-preview-frame__chrome" aria-hidden="true">
        <span className="shop-preview-frame__dot" />
        <span className="shop-preview-frame__dot" />
        <span className="shop-preview-frame__dot" />
        <span className="shop-preview-frame__url">
          {slug
            ? `ritaaa.fr/boutiques/${slug}`
            : 'ritaaa.fr/boutiques/votre-boutique'}
        </span>
      </div>

      <div className="shop-preview" style={previewStyle}>
        <button
          type="button"
          className={`shop-preview__banner ${zoneClass('banner')}`}
          onClick={(e) => handleZoneClick('banner', e)}
          aria-label="Modifier la bannière"
        >
          {bannerUrl ? (
            <img src={bannerUrl} alt="" className="shop-preview__banner-image" />
          ) : (
            <div className="shop-preview__banner-placeholder">
              <span>Bannière</span>
              <small>1200 × 400 px recommandé</small>
            </div>
          )}
          {interactive && <span className="shop-preview__zone-hint">Modifier la bannière</span>}
        </button>

        <div className="shop-preview__container">
          <div className="shop-preview__header">
            <button
              type="button"
              className={`shop-preview__logo-wrap ${zoneClass('logo')}`}
              onClick={(e) => handleZoneClick('logo', e)}
              aria-label="Modifier le logo"
            >
              {logoUrl ? (
                <img src={logoUrl} alt="" className="shop-preview__logo" />
              ) : (
                <div className="shop-preview__logo-placeholder">
                  <StoreIcon fontSize="large" />
                </div>
              )}
              {interactive && <span className="shop-preview__zone-hint">Logo</span>}
            </button>

            <div
              className={`shop-preview__identity ${zoneClass('name')}`}
              onClick={(e) => handleZoneClick('name', e)}
              onKeyDown={() => {}}
              role="presentation"
            >
              {interactive ? (
                <>
                  <input
                    type="text"
                    className="shop-preview__name-input"
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                    onFocus={() => onFieldSelect('name')}
                    placeholder="Nom de votre boutique"
                    aria-label="Nom de la boutique"
                  />
                  <input
                    type="text"
                    className={`shop-preview__title-input ${zoneClass('title')}`}
                    value={title}
                    onChange={(e) => onTitleChange(e.target.value)}
                    onFocus={() => onFieldSelect('title')}
                    placeholder="Votre slogan ou accroche"
                    aria-label="Titre de la boutique"
                  />
                </>
              ) : (
                <>
                  <h1 className="shop-preview__name">{displayName}</h1>
                  <p className="shop-preview__title">{displayTitle}</p>
                </>
              )}
            </div>
          </div>

          <div className="shop-preview__back-link" aria-hidden="true">
            ← Retour à l&apos;accueil
          </div>

          <section
            className={`shop-preview__products ${zoneClass('products')}`}
            onClick={(e) => handleZoneClick('products', e)}
            aria-label="Aperçu des produits"
          >
            <h2 className="shop-preview__products-title">Créations</h2>

            {showLayoutToolbar && (
              <div className="shop-preview__toolbar" aria-hidden="true">
                {layout.showSearch && <span className="shop-preview__toolbar-item">Recherche</span>}
                {layout.showFilters && <span className="shop-preview__toolbar-item">Filtres</span>}
                {layout.showCategories && layout.categoriesPosition === 'top' && (
                  <span className="shop-preview__toolbar-item">Catégories</span>
                )}
              </div>
            )}

            <div
              className={`shop-preview__products-layout ${
                layout.showCategories && layout.categoriesPosition === 'sidebar'
                  ? 'shop-preview__products-layout--sidebar'
                  : ''
              }`}
            >
              {layout.showCategories && layout.categoriesPosition === 'sidebar' && (
                <aside className="shop-preview__categories" aria-hidden="true">
                  <span>Catégories</span>
                  <span>Décoration</span>
                  <span>Vaisselle</span>
                </aside>
              )}

              <div className="shop-preview__products-grid">
                {PLACEHOLDER_PRODUCTS.map((product) => (
                  <div
                    key={product.name}
                    className={`shop-preview__product-card shop-preview__product-card--${productDisplay.hoverEffect}`}
                  >
                    <div className="shop-preview__product-image" />
                    <div className="shop-preview__product-body">
                      <span className="shop-preview__product-name">{product.name}</span>
                      {productDisplay.showDescription && (
                        <span className="shop-preview__product-desc">{product.description}</span>
                      )}
                      {productDisplay.showPrice && (
                        <span className="shop-preview__product-price">{product.price}</span>
                      )}
                      {productDisplay.showStock && (
                        <span className="shop-preview__product-stock">En stock</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="shop-preview__products-note">
              Aperçu des réglages d&apos;affichage — vos produits apparaîtront ici sur la page publique
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ShopAppearancePreview;
